import { reportHandledException } from '@/lib/error-reporting'
import type { DirectInitiateRequest } from '@kiao-voice/api-contract/anonymous-trial'
import { sniffFile } from './trim/sniff'
import type { AnonymousSubmitPhase } from './trial-flow'
import { ApiError } from '@kiao-voice/api-client'
import { getWebApiBaseUrl } from '../../lib/api'
import { sendXhrUpload, XhrUploadStalledError } from '../../lib/xhr-upload'
import {
  createWebApiClient,
  createWebPublicApiClient,
  getWebApiClient,
  getWebPublicApiClient,
} from '../../lib/web-api-client'

import type {
  AnonymousClaimResponse,
  AnonymousTrialErrorCode,
  AnonymousTrialResult,
  AnonymousTrialSubmitResponse,
  DirectInitiateResponse,
} from './types'

interface SubmitOpts {
  /** Base URL override (test seam) — submit stays hand-rolled XHR for upload
   *  progress, so it keeps its own base resolution. */
  baseUrl?: string
  onUploadProgress?: (fraction: number) => void
  /** Cancel the in-flight upload (wired to the UI's cancel button). */
  signal?: AbortSignal
  /**
   * Abort when NO upload bytes move for this long — a stalled connection
   * must become an actionable, retryable error, never an indefinite spinner.
   */
  stallTimeoutMs?: number
  /** Reports which submit phase is running so the UI can narrate it. */
  onPhase?: (phase: AnonymousSubmitPhase) => void
  /**
   * Cut the first `secs` seconds out of the file before uploading it. Injected
   * (rather than imported here) so the trimmer — and the MP4 parser it pulls in
   * — stays out of this module's chunk and is only fetched once a file exists.
   *
   * `secs` comes from the direct-initiate handshake, so the trim can only
   * happen on the direct path. The proxy fallback reuses whatever came out of
   * it rather than re-reading the original file.
   */
  trimToSecs?: (file: File, secs: number) => Promise<TrimmedUpload>
  /**
   * The ORIGINAL media's duration, when the browser trimmed the file before
   * uploading it. Without it the server would probe the trimmed upload and
   * report a five-minute file as the visitor's whole recording.
   */
  originalDurationSecs?: number | null
}

/** What actually goes up, plus what only the browser could know about it. */
export interface TrimmedUpload {
  file: File
  trimmed: boolean
  /** Duration of the file BEFORE trimming; null when it could not be read. */
  sourceDurationSecs: number | null
}

/** Per-call options for the client-backed calls. `fetchFn` injects a mock. */
interface RequestOpts {
  fetchFn?: typeof fetch
}

export class AnonymousTrialApiError extends Error {
  readonly status: number
  readonly code: AnonymousTrialErrorCode
  readonly serverMessage: string | null
  readonly extras: Record<string, unknown>

  constructor(init: {
    message: string
    status: number
    code: AnonymousTrialErrorCode
    serverMessage?: string | null
    extras?: Record<string, unknown>
  }) {
    super(init.message)
    this.name = 'AnonymousTrialApiError'
    this.status = init.status
    this.code = init.code
    this.serverMessage = init.serverMessage ?? null
    this.extras = init.extras ?? {}
  }
}

function resolveBase(opts: SubmitOpts): string {
  return opts.baseUrl ?? getWebApiBaseUrl()
}

/**
 * Map a thrown `ApiError` from the shared client to `AnonymousTrialApiError`,
 * preserving the body-derived code / serverMessage / extras the UI branches on.
 * Non-ApiError values (network failures) propagate.
 */
function trialErrorFromApi(
  error: unknown,
  fallbackFor: (status: number) => AnonymousTrialErrorCode
): unknown {
  if (!(error instanceof ApiError)) return error
  const body =
    error.body && typeof error.body === 'object' ? (error.body as Record<string, unknown>) : {}
  const code = (
    typeof body.error === 'string' ? body.error : fallbackFor(error.status)
  ) as AnonymousTrialErrorCode
  const serverMessage = typeof body.message === 'string' ? body.message : null
  return new AnonymousTrialApiError({
    message: serverMessage ?? `Request failed (HTTP ${error.status}).`,
    status: error.status,
    code,
    serverMessage,
    extras: body,
  })
}

async function parseErrorBody(
  response: Response,
  fallbackCode: AnonymousTrialErrorCode
): Promise<{
  code: AnonymousTrialErrorCode
  message: string
  serverMessage: string | null
  extras: Record<string, unknown>
}> {
  try {
    const body = (await response.json()) as Record<string, unknown>
    const code = (typeof body.error === 'string' ? body.error : fallbackCode) as
      | AnonymousTrialErrorCode
      | string
    const serverMessage = typeof body.message === 'string' ? body.message : null
    return {
      code: (code as AnonymousTrialErrorCode) ?? fallbackCode,
      message: serverMessage ?? `Request failed (HTTP ${response.status}).`,
      serverMessage,
      extras: body,
    }
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/anonymous-trial/api-client.ts' })
    return {
      code: fallbackCode,
      message: `Request failed (HTTP ${response.status}).`,
      serverMessage: null,
      extras: {},
    }
  }
}

/** Translate the shared stall signal into this feature's error taxonomy. */
function rejectAsTrialError(reject: (reason: unknown) => void): (reason: unknown) => void {
  return (reason) => {
    if (reason instanceof XhrUploadStalledError) {
      reject(
        new AnonymousTrialApiError({
          message: reason.message,
          status: 0,
          code: 'upload_stalled',
        })
      )
      return
    }
    reject(reason)
  }
}

function postFormDataWithXhr(
  url: string,
  formData: FormData,
  opts: Pick<SubmitOpts, 'onUploadProgress' | 'signal' | 'stallTimeoutMs' | 'onPhase'> = {}
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    sendXhrUpload(xhr, formData, {
      ...opts,
      // 進度條滿了不等於做完：body 送完之後伺服器還要寫 R2、探測時長、建 row，
      // 大一點的檔這段可以超過一分鐘。停在「上傳中 100%」看起來像當掉，所以
      // 這裡交棒給既有的 finalizing 階段（「檔案處理中，即將完成…」）。
      onBodySent: () => opts.onPhase?.('finalizing'),
      resolve,
      reject: rejectAsTrialError(reject),
    })
  })
}

/** Direct-to-R2 presigned PUT with the same progress/cancel/stall handling. */
function putFileWithXhr(
  url: string,
  file: File,
  contentType: string,
  opts: Pick<SubmitOpts, 'onUploadProgress' | 'signal' | 'stallTimeoutMs' | 'onPhase'> = {}
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    // Must match the content type pinned into the presigned signature.
    xhr.setRequestHeader('Content-Type', contentType)
    sendXhrUpload(xhr, file, {
      ...opts,
      // 同樣的交棒：R2 收完 body 還要落地才回 200，緊接著的 complete 也屬於
      // 「處理中」，所以這兩段在畫面上是同一格。
      onBodySent: () => opts.onPhase?.('finalizing'),
      resolve,
      reject: rejectAsTrialError(reject),
    })
  })
}

/**
 * Submit a single audio file as an anonymous trial through the API (multipart).
 *
 * Length is NOT a rejection reason: the server accepts any duration and
 * transcribes only the first `anonymous_trial.trim_secs` seconds, so
 * `probeFileDurationSecs` exists only to show the file's length on screen.
 */
export async function submitAnonymousTrial(
  file: File,
  opts: SubmitOpts = {}
): Promise<AnonymousTrialSubmitResponse> {
  const formData = new FormData()
  formData.append('files', file, file.name)
  // Only sent when the browser trimmed: otherwise the server's own ffprobe of
  // the uploaded bytes IS the original duration, and is the trustworthier of
  // the two.
  if (typeof opts.originalDurationSecs === 'number' && opts.originalDurationSecs > 0) {
    formData.append('originalDurationSecs', String(opts.originalDurationSecs))
  }

  opts.onPhase?.('uploading')
  const response = await postFormDataWithXhr(
    `${resolveBase(opts)}/anonymous/batch-transcriptions`,
    formData,
    opts
  )

  if (!response.ok) await throwTrialError(response)

  return (await response.json()) as AnonymousTrialSubmitResponse
}

async function throwTrialError(
  response: Response,
  phase?: 'initiate' | 'complete'
): Promise<never> {
  const parsed = await parseErrorBody(response, 'unknown')
  throw new AnonymousTrialApiError({
    message: parsed.message,
    status: response.status,
    code: parsed.code,
    serverMessage: parsed.serverMessage,
    extras: phase ? { ...parsed.extras, phase } : parsed.extras,
  })
}

/**
 * Browser-direct submit: initiate → presigned PUT straight to R2 → complete.
 * The audio bytes never transit the API origin — the proxy path's extra hop
 * is both slower and the fragile link for uploads, which is why direct is
 * the preferred transport and the proxy is only the fallback.
 */
export async function submitAnonymousTrialDirect(
  file: File,
  opts: SubmitOpts = {}
): Promise<AnonymousTrialSubmitResponse> {
  const base = resolveBase(opts)
  const initiated = await initiateDirectUpload(base, file, opts)
  const upload = await prepareUpload(file, initiated.trimSecs, opts)
  return finishDirectUpload(base, initiated, upload, opts)
}

/** First leg: hand the server the file's shape, get back where to PUT it. */
async function initiateDirectUpload(
  base: string,
  file: File,
  opts: SubmitOpts
): Promise<DirectInitiateResponse> {
  opts.onPhase?.('preparing')
  const clientTrim = opts.trimToSecs ? await sniffFile(file) : null
  if (opts.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError')
  const body: DirectInitiateRequest = {
    filename: file.name,
    size: file.size,
    mimeType: file.type || null,
    clientTrim,
  }
  const initiateResponse = await fetch(`${base}/anonymous/batch-transcriptions/direct/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: opts.signal,
    body: JSON.stringify(body),
  })
  if (!initiateResponse.ok) await throwTrialError(initiateResponse, 'initiate')
  return (await initiateResponse.json()) as DirectInitiateResponse
}

/**
 * Cut the file down to what the trial will actually transcribe.
 *
 * A few seconds of slack are added on purpose: the server cuts at exactly
 * `trimSecs`, and handing it a file that ends on that same instant risks
 * losing the last word to a boundary rounding either side of the wire.
 */
const TRIM_MARGIN_SECS = 5

async function prepareUpload(
  file: File,
  trimSecs: number,
  opts: SubmitOpts
): Promise<TrimmedUpload> {
  const untrimmed: TrimmedUpload = { file, trimmed: false, sourceDurationSecs: null }
  if (!opts.trimToSecs || !(trimSecs > 0)) return untrimmed

  opts.onPhase?.('trimming')
  const prepared = await opts.trimToSecs(file, trimSecs + TRIM_MARGIN_SECS)
  // The trimmer has no abort signal of its own; a visitor who cancelled while
  // it ran must not then watch the upload start.
  if (opts.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError')
  return prepared
}

/** Second and third legs: the presigned PUT, then the completion handshake. */
async function finishDirectUpload(
  base: string,
  initiated: DirectInitiateResponse,
  upload: TrimmedUpload,
  opts: SubmitOpts
): Promise<AnonymousTrialSubmitResponse> {
  const file = upload.file
  // Even a successful trim can exceed the limit (e.g. multichannel PCM).
  const maxBytes = initiated.maxObjectBytes ?? initiated.maxFileBytes
  if (file.size > maxBytes) {
    throw new AnonymousTrialApiError({
      message: 'The prepared file exceeds the free trial size limit.',
      status: 413,
      code: 'file_too_large',
      extras: { maxBytes },
    })
  }

  opts.onPhase?.('uploading')
  const putResponse = await putFileWithXhr(initiated.uploadUrl, file, initiated.contentType, opts)
  if (!putResponse.ok) {
    // Client-side-only code: distinguishes "the R2 PUT was rejected" (fall
    // back to the proxy path) from the server's own `upload_failed` (do not).
    throw new AnonymousTrialApiError({
      message: `Direct upload failed (HTTP ${putResponse.status}).`,
      status: putResponse.status,
      code: 'direct_put_failed',
      extras: { phase: 'put' },
    })
  }

  opts.onPhase?.('finalizing')
  // Complete is idempotent server-side (UNIQUE shortId), so BOTH a lost
  // response (network rejection) and a transient 5xx from an intermediary
  // are safe to retry with the same shortId. When every attempt fails the
  // error is surfaced tagged `phase: 'complete'` — the smart wrapper must
  // NOT fall back to a full proxy re-upload at that point, because the
  // server may already hold the trial and a re-upload would only burn the
  // visitor's daily quota a second time.
  let lastError: unknown = null
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (opts.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError')
    try {
      // Per-attempt timeout: a black-holed connection (no RST) must fail this
      // attempt and move to the retry, not hang the finalizing phase forever.
      const attemptSignal =
        typeof AbortSignal.any === 'function'
          ? opts.signal
            ? AbortSignal.any([opts.signal, AbortSignal.timeout(30_000)])
            : AbortSignal.timeout(30_000)
          : opts.signal
      const completeResponse = await fetch(
        `${base}/anonymous/batch-transcriptions/direct/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: attemptSignal,
          body: JSON.stringify({
            shortId: initiated.shortId,
            objectKey: initiated.objectKey,
            filename: file.name,
            ...originalDurationBody(upload),
          }),
        }
      )
      if (completeResponse.status >= 500) {
        // A 5xx can still carry a meaningful verdict (e.g. 503
        // anonymous_trial_disabled). Two codes stay retryable: `unknown`
        // (edge/proxy garbage) and `upload_failed` — the server's own
        // "transient verification failure, staging object kept" answer,
        // which exists precisely so the same shortId can try again.
        const parsed = await parseErrorBody(completeResponse, 'unknown')
        const parsedError = new AnonymousTrialApiError({
          message: parsed.message,
          status: completeResponse.status,
          code: parsed.code,
          serverMessage: parsed.serverMessage,
          extras: { ...parsed.extras, phase: 'complete' },
        })
        if (parsed.code !== 'unknown' && parsed.code !== 'upload_failed') throw parsedError
        lastError = parsedError
        if (attempt < 2) {
          await new Promise((resolveDelay) => setTimeout(resolveDelay, 1000 * (attempt + 1)))
        }
        continue
      }
      if (!completeResponse.ok) await throwTrialError(completeResponse, 'complete')
      return (await completeResponse.json()) as AnonymousTrialSubmitResponse
    } catch (error) {
      if (error instanceof AnonymousTrialApiError) throw error
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      reportHandledException(error, { source: 'features/anonymous-trial/api-client.ts' })
      lastError = error
      if (attempt < 2) {
        await new Promise((resolveDelay) => setTimeout(resolveDelay, 1000 * (attempt + 1)))
      }
    }
  }
  if (lastError instanceof AnonymousTrialApiError) throw lastError
  throw new AnonymousTrialApiError({
    message: 'Completion did not get a response.',
    status: 0,
    code: 'unknown',
    extras: { phase: 'complete' },
  })
}

/**
 * The original duration, but only when a trim actually happened. An untrimmed
 * upload IS the original, so letting the client declare a length there would
 * replace a server measurement with a client claim for no gain.
 */
function originalDurationBody(upload: TrimmedUpload): { originalDurationSecs?: number } {
  if (!upload.trimmed) return {}
  const secs = upload.sourceDurationSecs
  return typeof secs === 'number' && Number.isFinite(secs) && secs > 0
    ? { originalDurationSecs: Math.round(secs) }
    : {}
}

/** Legacy multipart ceiling, matching ANONYMOUS_TRIAL_PROXY_MAX_FILE_BYTES on the API. */
const PROXY_MAX_FILE_BYTES = 100 * 1024 * 1024

/**
 * The submit entrypoint the page uses: try the direct-to-R2 path, fall back
 * to the legacy proxy upload when direct is unavailable — the kill switch is
 * off (503 direct_upload_disabled), the API predates the endpoint (404), or
 * the R2 PUT itself fails/stalls. Real verdicts (rate limit, unsupported
 * file, trial disabled) propagate without a fallback: the answer would be
 * the same on either path.
 */
export async function submitAnonymousTrialSmart(
  file: File,
  opts: SubmitOpts = {}
): Promise<AnonymousTrialSubmitResponse> {
  // Whatever the direct path managed to prepare before it failed. The trim can
  // only start once initiate has answered with `trimSecs`, so a failure at or
  // before initiate leaves this as the original file — and the proxy fallback
  // then uploads the whole thing, exactly as it did before trimming existed.
  let upload: TrimmedUpload = { file, trimmed: false, sourceDurationSecs: null }
  try {
    const base = resolveBase(opts)
    const initiated = await initiateDirectUpload(base, file, opts)
    upload = await prepareUpload(file, initiated.trimSecs, opts)
    return await finishDirectUpload(base, initiated, upload, opts)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof AnonymousTrialApiError) {
      const phase = error.extras.phase
      // Anything at or after complete stays final: the server may already
      // hold this shortId's trial, so re-uploading through the proxy could
      // only double-spend the visitor's quota.
      if (phase === 'complete') throw error
      // An initiate 429 means two different things: the direct path's burst
      // throttle (retryAfterSecs — legacy has no such throttle, so fall back)
      // versus the authoritative daily quota (retryAfterHours — final on both
      // paths; falling back would re-upload the whole file just to hear the
      // same verdict).
      const throttled429 =
        error.status === 429 &&
        'retryAfterSecs' in error.extras &&
        !('retryAfterHours' in error.extras)
      const fallbackWorthy =
        error.code === 'direct_upload_disabled' ||
        error.code === 'upload_stalled' ||
        error.code === 'direct_put_failed' ||
        // Initiate-scoped transport/availability problems: endpoint missing
        // (older API) or erroring — the legacy path may still work.
        (phase === 'initiate' && (error.status === 404 || throttled429 || error.status >= 500))
      if (!fallbackWorthy) throw error
    }
    // Initiate may fail before trimming. Never send a file the proxy cannot
    // accept, including a successfully trimmed file still above its ceiling.
    if (upload.file.size > PROXY_MAX_FILE_BYTES) throw error
    // Direct path unavailable or broken — retry once through the legacy
    // proxy upload (which carries the same stall/cancel guards). It uploads
    // the trimmed file when there is one: re-sending the original would be
    // the very transfer the trim exists to avoid.
    reportHandledException(error, { source: 'features/anonymous-trial/api-client.ts' })
    return await submitAnonymousTrial(upload.file, {
      ...opts,
      ...(upload.trimmed ? originalDurationBody(upload) : {}),
      // The trim already ran; a second pass over an already-trimmed file would
      // only re-read it to conclude it is short enough.
      trimToSecs: undefined,
    })
  }
}

/** Public read — the anonymous result is fetched without auth/context. */
export async function fetchAnonymousResult(
  shortId: string,
  opts: RequestOpts = {}
): Promise<AnonymousTrialResult> {
  const client = opts.fetchFn ? createWebPublicApiClient(opts.fetchFn) : getWebPublicApiClient()
  try {
    return await client.request<AnonymousTrialResult>(
      `/api/v1/anonymous/results/${encodeURIComponent(shortId)}`,
      { method: 'GET' }
    )
  } catch (error) {
    throw trialErrorFromApi(error, (status) =>
      status === 404 ? 'not_found' : status === 410 ? 'expired' : 'unknown'
    )
  }
}

/** Authed — claim the anonymous trial onto the signed-in account. */
export async function claimAnonymousTrial(
  shortId: string,
  opts: RequestOpts = {}
): Promise<AnonymousClaimResponse> {
  const client = opts.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
  try {
    return await client.request<AnonymousClaimResponse>('/api/v1/anonymous/claim', {
      method: 'POST',
      body: JSON.stringify({ shortId }),
    })
  } catch (error) {
    throw trialErrorFromApi(error, (status) =>
      status === 404
        ? 'not_found'
        : status === 410
          ? 'expired'
          : status === 409
            ? 'already_claimed'
            : 'unknown'
    )
  }
}

/**
 * Browser-side duration probe via HTMLAudioElement. Resolves with the
 * duration in seconds, `null` if the browser can't decode the file
 * (server-side ffprobe will run as the authoritative check). Uses an
 * object URL so the audio data never leaves the page until submit.
 *
 * The 10-second timeout guards against weird files that put the
 * `loadedmetadata` event into never-firing limbo.
 */
export function probeFileDurationSecs(file: File, timeoutMs = 10_000): Promise<number | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    const audio = document.createElement('audio')
    audio.preload = 'metadata'
    const url = URL.createObjectURL(file)
    const cleanup = () => {
      URL.revokeObjectURL(url)
      audio.removeEventListener('loadedmetadata', onLoaded)
      audio.removeEventListener('error', onError)
    }
    const timer = setTimeout(() => {
      cleanup()
      resolve(null)
    }, timeoutMs)
    const onLoaded = () => {
      clearTimeout(timer)
      const d = Number(audio.duration)
      cleanup()
      resolve(Number.isFinite(d) && d > 0 ? d : null)
    }
    const onError = () => {
      clearTimeout(timer)
      cleanup()
      resolve(null)
    }
    audio.addEventListener('loadedmetadata', onLoaded)
    audio.addEventListener('error', onError)
    audio.src = url
  })
}
